import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { MenuIcon } from 'components/icons';
import CallIcon from '@mui/icons-material/Call';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import UploadIcon from '@mui/icons-material/Upload';
import ReportIcon from '@mui/icons-material/Report';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Badge, Paper, Tooltip } from '@mui/material';
import { GuardTabs } from 'pages/CANguard/CANguard';
import { format } from 'date-fns';
import Link from '@mui/material/Link';
import styles from './GuardBar.module.css';
import CircleIcon from '@mui/icons-material/Circle';
import { useActivePatrolData } from '../../context/activePatrol/ActivePatrolDataProvider';
import { useActivePatrolUI } from '../../context/activePatrol/ActivePatrolUIProvider';
import { useGuardRoleContext } from '../../context/GuardRoleProvider';
import { useCancam } from '../../context/cancam/CancamProvider';
interface GuardBarProps {
	activeTab: GuardTabs;
	handleMenuClick: () => void;
	handleTabChange: (tab: GuardTabs) => void;
}
interface ActivePatrolBarProps {
	customer: string;
	address: string;
	onClick: () => void;
}
const ActivePatrolBar = ({
	customer,
	address,
	onClick,
}: ActivePatrolBarProps) => (
	<Paper className={styles.activePatrolBar} onClick={onClick}>
		<CircleIcon color="error" fontSize="small" className={styles.pulse} />
		<Typography variant="body2" className={styles.activePatrolBarText}>
			Active patrol: {customer} {address}
		</Typography>
	</Paper>
);

export default function GuardBar({
	handleMenuClick,
	activeTab,
	handleTabChange,
}: GuardBarProps) {
	const { isAdmin, guardSelectorOptions } = useGuardRoleContext();
	const { activePatrolDataRef } = useActivePatrolData();
	const { setShowDrawer } = useActivePatrolUI();
	const { openDrawer: openCancamDrawer, deviceState } = useCancam();

	const disableNav = isAdmin && !guardSelectorOptions.selectedGuardName;

	return (
		<Box>
			<AppBar position="static" color="default">
				{activePatrolDataRef.current && (
					<ActivePatrolBar
						customer={
							activePatrolDataRef.current.patrolInfo.cust_name
						}
						address={activePatrolDataRef.current.patrolInfo.address}
						onClick={() => setShowDrawer(true)}
					/>
				)}

				<Toolbar
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: { xs: 'space-between', md: 'center' },
						alignItems: 'center',
					}}
				>
					<Box sx={{ display: { xs: 'flex', md: 'flex' } }}>
						<IconButton
							size="large"
							aria-label="show menu"
							onClick={handleMenuClick}
							color="inherit"
						>
							<MenuIcon />
						</IconButton>
					</Box>

					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							overflowX: 'auto',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						{isAdmin && (
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
								}}
							>
								<Tooltip title="Uploads">
									<IconButton
										size="large"
										color="inherit"
										onClick={() =>
											handleTabChange(GuardTabs.Upload)
										}
									>
										<UploadIcon
											color={
												activeTab === GuardTabs.Upload
													? 'primary'
													: 'inherit'
											}
										/>
									</IconButton>
								</Tooltip>
								<Tooltip title="AdminPanel">
									<IconButton
										size="large"
										color={
											activeTab === GuardTabs.AdminPanel
												? 'primary'
												: 'inherit'
										}
										onClick={() =>
											handleTabChange(
												GuardTabs.AdminPanel,
											)
										}
									>
										<AdminPanelSettingsIcon />
									</IconButton>
								</Tooltip>
								<Typography noWrap>
									{guardSelectorOptions.selectedGuardName &&
									guardSelectorOptions.selectedDate ? (
										<>
											You are viewing{' '}
											<Link
												onClick={() =>
													handleTabChange(
														GuardTabs.AdminPanel,
													)
												}
											>
												{
													guardSelectorOptions.selectedGuardName
												}
												's
											</Link>{' '}
											schedule for{' '}
											<Link
												onClick={() =>
													handleTabChange(
														GuardTabs.AdminPanel,
													)
												}
											>
												{format(
													guardSelectorOptions.selectedDate,
													'EEE, d MMM yyyy',
												)}
											</Link>
										</>
									) : (
										`Select a guard to view their schedule`
									)}
								</Typography>
							</Box>
						)}

						<Box
							sx={{
								display: 'flex',
								gap: 1,
								width: `${isAdmin ? 'fit-content' : '100%'}`,
								justifyContent: 'center',
							}}
						>
							<Tooltip title={disableNav ? 'Select a guard first' : 'Notifications'}>
								<span>
								<IconButton
									size="large"
									color="inherit"
									onClick={() =>
										handleTabChange(GuardTabs.Notifications)
									}
									disabled={disableNav}
								>
									<NotificationsIcon
										color={
											activeTab ===
											GuardTabs.Notifications
												? 'primary'
												: 'inherit'
										}
									/>
								</IconButton>
								</span>
							</Tooltip>

							<Tooltip title={disableNav ? 'Select a guard first' : 'Schedule'}>
								<span>
								<IconButton
									size="large"
									color={
										activeTab === GuardTabs.Schedule
											? 'primary'
											: 'inherit'
									}
									onClick={() =>
										handleTabChange(GuardTabs.Schedule)
									}
									disabled={disableNav}
								>
									<CalendarMonthIcon />
								</IconButton>
								</span>
							</Tooltip>
							<Tooltip title={disableNav ? 'Select a guard first' : 'Patrols'}>
								<span>
								<IconButton
									size="large"
									color={
										activeTab === GuardTabs.Patrols
											? 'primary'
											: 'inherit'
									}
									onClick={() =>
										handleTabChange(GuardTabs.Patrols)
									}
									disabled={disableNav}
								>
									<EmojiTransportationIcon />
								</IconButton>
								</span>
							</Tooltip>

							<Tooltip title={disableNav ? 'Select a guard first' : 'Call'}>
								<span>
								<IconButton
									size="large"
									color={
										activeTab === GuardTabs.Call
											? 'primary'
											: 'inherit'
									}
									onClick={() =>
										handleTabChange(GuardTabs.Call)
									}
									disabled={disableNav}
								>
									<CallIcon />
								</IconButton>
								</span>
							</Tooltip>

							<Tooltip title={disableNav ? 'Select a guard first' : 'Report Incident'}>
								<span>
								<IconButton
									size="large"
									color={
										activeTab === GuardTabs.Incident
											? 'primary'
											: 'inherit'
									}
									onClick={() =>
										handleTabChange(GuardTabs.Incident)
									}
									disabled={disableNav}
								>
									<ReportIcon />
								</IconButton>
								</span>
							</Tooltip>

							<Tooltip title={disableNav ? 'Select a guard first' : 'CANcam'}>
								<span>
								<IconButton
									size="large"
									color="inherit"
									onClick={openCancamDrawer}
									disabled={disableNav}
								>
									<Badge
										color="error"
										variant="dot"
										invisible={!deviceState?.isRecording}
									>
										<VideocamIcon />
									</Badge>
								</IconButton>
								</span>
							</Tooltip>
						</Box>
					</Box>
				</Toolbar>
			</AppBar>
		</Box>
	);
}
